// overlayApi.js — thin fetch helpers for the #57 stream_id-keyed overlay plane
// (POST /api/ledger/overlay, POST /api/ledger/overlay/{id}/revert, GET /api/ledger/overlays).
//
// Every mutation goes through ledgerFetch (not raw fetch) so the existing demo-tenant shim
// keeps working unchanged — see lib/ledgerDemoSource.js. No re-projection/derivation here;
// these are dumb HTTP calls only, matching the overlay contract in backend/services/pg_overlay_service.py.

import { ledgerFetch } from "@/lib/ledgerDemoSource";

// Server-side cap is 500 (pg_overlay_service._NOTE_MAX_LEN); mirrored here as a client-side
// safety net so an over-long note never round-trips to a 400 the user can't see coming.
export const NOTE_MAX_LEN = 500;

export function clampNote(note) {
  if (note == null) return undefined;
  const trimmed = String(note).trim();
  return trimmed ? trimmed.slice(0, NOTE_MAX_LEN) : undefined;
}

async function _asJsonOrThrow(res) {
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}${txt ? ": " + txt.slice(0, 160) : ""}`);
  }
  return res.json();
}

// POST /api/ledger/overlay — add one overlay row (discard/field_override/exclude_doc/merge_fund/link_member).
export async function addOverlay({ streamId, kind, payload, note, token }) {
  const res = await ledgerFetch("/api/ledger/overlay", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      stream_id: streamId,
      kind,
      payload: payload || {},
      note: clampNote(note),
    }),
  });
  return _asJsonOrThrow(res);
}

// POST /api/ledger/overlay/{overlay_id}/revert — non-destructive undo.
export async function revertOverlay(overlayId, token) {
  const res = await ledgerFetch(`/api/ledger/overlay/${overlayId}/revert`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  return _asJsonOrThrow(res);
}

// GET /api/ledger/overlays?stream_id=<optional> — full history (active + reverted).
// Degrades to [] on any failure — overlay history is a display/undo aid, never a hard dependency.
export async function listOverlays(streamId, token) {
  const qs = streamId ? `?stream_id=${encodeURIComponent(streamId)}` : "";
  try {
    const res = await ledgerFetch(`/api/ledger/overlays${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.overlays || [];
  } catch {
    return [];
  }
}

// Find the single ACTIVE overlay of `kind` for a stream (optionally scoped to payload.field,
// for field_override) — the lookup the Undo/revert affordances need since neither the overview
// tree nor the discarded list carries overlay ids directly.
export async function findActiveOverlay(streamId, kind, token, { field } = {}) {
  const overlays = await listOverlays(streamId, token);
  return (
    overlays.find(
      (o) =>
        o.status === "active" &&
        o.kind === kind &&
        (field == null || (o.payload || {}).field === field)
    ) || null
  );
}

// Revert every active overlay of `kind` across a list of stream_ids (best-effort, one lookup +
// one revert per stream). Used by "Unmerge" / "Unlink" which must undo N per-stream overlay rows
// created by a single merge/link action. Never throws — a stream with no matching overlay is a
// silent no-op (mirrors the backend's own idempotent-upsert posture elsewhere in this plane).
export async function revertOverlaysForStreams(streamIds, kind, token, { field } = {}) {
  const results = await Promise.all(
    (streamIds || []).map(async (sid) => {
      const overlay = await findActiveOverlay(sid, kind, token, { field });
      if (!overlay) return null;
      return revertOverlay(overlay.id, token);
    })
  );
  return results.filter(Boolean);
}
